// src/utils/exportAnnotatedDicom.js
import dcmjs from "dcmjs";
import { validateAnnotationsForExport, AnnotationValidationError } from "./annotationValidator.js";

/**
 * Custom error classes for better error handling
 */
export class DicomExportError extends Error {
  constructor(message, code, details = {}) {
    super(message);
    this.name = 'DicomExportError';
    this.code = code;
    this.details = details;
  }
}

export class DicomValidationError extends DicomExportError {
  constructor(message, details = {}) {
    super(message, 'VALIDATION_ERROR', details);
    this.name = 'DicomValidationError';
  }
}

export class DicomCorruptionError extends DicomExportError {
  constructor(message, details = {}) {
    super(message, 'CORRUPTION_ERROR', details);
    this.name = 'DicomCorruptionError';
  }
}

export class DicomProcessingError extends DicomExportError {
  constructor(message, details = {}) {
    super(message, 'PROCESSING_ERROR', details);
    this.name = 'DicomProcessingError';
  }
}

/**
 * Validates DICOM file structure and content
 */
function validateDicomFile(file) {
  if (!file) {
    throw new DicomValidationError("No DICOM file provided", { 
      userMessage: "Please select a DICOM file to export" 
    });
  }

  if (file.size === 0) {
    throw new DicomValidationError("DICOM file is empty", { 
      userMessage: "The selected file appears to be empty. Please choose a valid DICOM file." 
    });
  }

  if (file.size > 500 * 1024 * 1024) { // 500MB limit
    throw new DicomValidationError("DICOM file is too large", { 
      userMessage: "The selected file is too large (>500MB). Please choose a smaller DICOM file.",
      fileSize: file.size 
    });
  }

  // Check file extension (optional but helpful)
  const validExtensions = ['.dcm', '.dicom', '.dic'];
  const hasValidExtension = validExtensions.some(ext => 
    file.name.toLowerCase().endsWith(ext)
  );
  
  if (!hasValidExtension && file.type !== 'application/dicom') {
    console.warn("File may not be a DICOM file based on extension/type", {
      name: file.name,
      type: file.type
    });
  }
}

/**
 * Validates DICOM message structure
 */
function validateDicomMessage(dicomMessage) {
  if (!dicomMessage) {
    throw new DicomCorruptionError("Failed to parse DICOM file", {
      userMessage: "The file appears to be corrupted or is not a valid DICOM file."
    });
  }

  if (!dicomMessage.dict) {
    throw new DicomCorruptionError("DICOM file missing data dictionary", {
      userMessage: "The DICOM file structure is invalid - missing required data dictionary."
    });
  }

  // Check for essential DICOM elements
  const dataset = dcmjs.data.DicomMetaDictionary.naturalizeDataset(dicomMessage.dict);
  
  if (!dataset.SOPClassUID) {
    throw new DicomCorruptionError("DICOM file missing SOP Class UID", {
      userMessage: "The DICOM file is missing essential metadata (SOP Class UID)."
    });
  }

  if (!dataset.SOPInstanceUID) {
    throw new DicomCorruptionError("DICOM file missing SOP Instance UID", {
      userMessage: "The DICOM file is missing essential metadata (SOP Instance UID)."
    });
  }
}

/**
 * Validates image dimensions and metadata
 */
function validateImageMetadata(dataset) {
  const cols = parseInt(dataset.Columns || dataset.PixelMatrixColumns || 0, 10);
  const rows = parseInt(dataset.Rows || dataset.PixelMatrixRows || 0, 10);
  
  if (!cols || !rows || cols <= 0 || rows <= 0) {
    throw new DicomProcessingError(`Invalid image dimensions: ${cols}x${rows}`, {
      userMessage: "The DICOM file has invalid image dimensions and cannot be processed.",
      dimensions: { cols, rows }
    });
  }

  if (cols > 10000 || rows > 10000) {
    throw new DicomProcessingError(`Image dimensions too large: ${cols}x${rows}`, {
      userMessage: "The DICOM image is too large to process efficiently.",
      dimensions: { cols, rows }
    });
  }

  return { cols, rows };
}

/**
 * Validates and processes annotations using the comprehensive validator
 */
function validateAndProcessAnnotations(annotationsByFrame, imageMetadata, options = {}) {
  try {
    // Use the comprehensive annotation validator
    const validationOptions = {
      strictMode: false, // Allow export with warnings
      autoCorrect: true, // Auto-correct invalid coordinates
      requireLabel: true, // Require non-empty labels
      maxLabelLength: 100, // Reasonable label length limit
      allowEmptyFrames: true, // Allow frames with no annotations
      ...options.validationOptions
    };

    const validationResult = validateAnnotationsForExport(
      annotationsByFrame, 
      imageMetadata, 
      validationOptions
    );

    // Check if validation failed completely
    if (!validationResult.isValid && validationResult.stats.validAnnotations === 0) {
      throw new DicomValidationError(
        "No valid annotations found for export after validation",
        {
          userMessage: "All annotations failed validation. Please check your annotation data and try again.",
          validationErrors: validationResult.errors,
          validationWarnings: validationResult.warnings,
          stats: validationResult.stats
        }
      );
    }

    // Log validation results
    if (validationResult.warnings.length > 0) {
      console.warn(`Annotation validation completed with ${validationResult.warnings.length} warnings:`, 
        validationResult.warnings.map(w => `Frame ${w.frameIndex}: ${w.warning}`));
    }

    if (validationResult.errors.length > 0) {
      console.warn(`Annotation validation found ${validationResult.errors.length} errors (skipped):`, 
        validationResult.errors.map(e => `Frame ${e.frameIndex}: ${e.error}`));
    }

    // Return processed annotations and stats
    return {
      processed: validationResult.validAnnotations,
      totalAnnotations: validationResult.stats.validAnnotations,
      invalidAnnotations: validationResult.stats.invalidAnnotations,
      correctedAnnotations: validationResult.stats.correctedAnnotations,
      validationWarnings: validationResult.warnings.length,
      validationErrors: validationResult.errors.length
    };

  } catch (error) {
    if (error instanceof AnnotationValidationError) {
      // Convert validation errors to export errors with user-friendly messages
      throw new DicomValidationError(
        `Annotation validation failed: ${error.message}`,
        {
          userMessage: error.details?.userMessage || 
            "Annotation data validation failed. Please check your annotations and try again.",
          originalError: error,
          validationCode: error.code
        }
      );
    }
    
    // Re-throw other errors
    throw error;
  }
}



/**
 * In-place exporter: modifies dicomMessage.dict and writes Part-10.
 * annotationsByFrame: { frameIndex: [{ ix, iy, label }, ...], ... }
 * ix/iy are normalized coords in [0..1] relative to image width/height
 */
export async function exportAnnotatedDicom({ file, annotationsByFrame = {}, options = {} }) {
  const privateGroup = options.privateGroup || 0x0029;
  const privateCreator = (options.privateCreator || "MY_ANNOTATIONS").slice(0, 16);

  try {
    // Step 1: Validate input file
    validateDicomFile(file);

    // Step 2: Read and parse DICOM file
    let arrayBuffer;
    try {
      arrayBuffer = await file.arrayBuffer();
    } catch (error) {
      throw new DicomProcessingError("Failed to read DICOM file", {
        userMessage: "Unable to read the selected file. The file may be corrupted or inaccessible.",
        originalError: error.message
      });
    }

    let dicomMessage;
    try {
      dicomMessage = dcmjs.data.DicomMessage.readFile(arrayBuffer);
    } catch (error) {
      throw new DicomCorruptionError("Failed to parse DICOM file format", {
        userMessage: "The file is not a valid DICOM file or is corrupted. Please check the file format.",
        originalError: error.message
      });
    }

    // Step 3: Validate DICOM structure
    validateDicomMessage(dicomMessage);
    
    const dict = dicomMessage.dict;
    const meta = dicomMessage.meta;

    // Step 4: Create naturalized dataset and validate
    let datasetNatural;
    try {
      datasetNatural = dcmjs.data.DicomMetaDictionary.naturalizeDataset(dict);
    } catch (error) {
      throw new DicomProcessingError("Failed to process DICOM metadata", {
        userMessage: "Unable to process the DICOM file metadata. The file may have an unsupported format.",
        originalError: error.message
      });
    }

    // Debug information (safe now because datasetNatural exists)
    console.log("DICT sample keys:", Object.keys(dict).slice(0, 20));
    console.log("meta keys:", Object.keys(meta || {}));
    console.log("meta content:", meta);
    console.log("Rows/Cols from datasetNatural:", datasetNatural.Rows, datasetNatural.Columns);

    // Step 5: Validate image metadata and get dimensions
    const imageMetadata = validateImageMetadata(datasetNatural);
    const { cols, rows } = imageMetadata;
    
    console.log(`Processing DICOM export: ${cols}x${rows} pixels`);

    // Step 6: Validate and process annotations with comprehensive validation
    const annotationResult = validateAndProcessAnnotations(annotationsByFrame, imageMetadata, options);
    const { 
      processed: built, 
      totalAnnotations, 
      invalidAnnotations, 
      correctedAnnotations,
      validationWarnings,
      validationErrors
    } = annotationResult;

    console.log(`Successfully processed ${Object.keys(built).length} frames with ${totalAnnotations} total annotations`);
    
    if (correctedAnnotations > 0) {
      console.warn(`Warning: ${correctedAnnotations} annotations were automatically corrected`);
    }
    
    if (invalidAnnotations > 0) {
      console.warn(`Warning: ${invalidAnnotations} invalid annotations were skipped`);
    }

    if (validationWarnings > 0) {
      console.warn(`Warning: ${validationWarnings} validation warnings occurred`);
    }
    
    // Log coordinate transformation details for debugging
    if (Object.keys(built).length > 0) {
      const sampleFrame = Object.keys(built)[0];
      const sampleAnnotations = built[sampleFrame] || [];
      
      // Log original vs transformed coordinates for first annotation
      if (sampleAnnotations.length > 0 && annotationsByFrame[sampleFrame]) {
        const original = annotationsByFrame[sampleFrame][0];
        const transformed = sampleAnnotations[0];
        console.log("Coordinate transformation example:");
        console.log(`  Original normalized: (${original.ix}, ${original.iy})`);
        console.log(`  Transformed pixel: (${transformed.x}, ${transformed.y})`);
        console.log(`  Image dimensions: ${cols}x${rows}`);
      }
    }

    // Step 7: Build JSON payload with enhanced metadata
    let annotationsJson;
    try {
      const payload = {
        version: "1.0",
        generatedAt: new Date().toISOString(),
        sopInstanceUID: datasetNatural.SOPInstanceUID || "unknown",
        imageMetadata: {
          rows: rows,
          columns: cols,
          numberOfFrames: parseInt(datasetNatural.NumberOfFrames || "1", 10)
        },
        coordinateSystem: "pixel", // Annotations are stored in pixel coordinates
        annotations: built,
        processingInfo: {
          totalAnnotations,
          invalidAnnotations,
          correctedAnnotations: correctedAnnotations || 0,
          validationWarnings: validationWarnings || 0,
          validationErrors: validationErrors || 0,
          framesWithAnnotations: Object.keys(built).length
        }
      };
      
      annotationsJson = JSON.stringify(payload);
      console.log("Annotation JSON payload size:", annotationsJson.length, "characters");
      console.log("Annotation JSON preview:", annotationsJson.substring(0, 500) + "...");
    } catch (error) {
      throw new DicomProcessingError("Failed to serialize annotation data", {
        userMessage: "Unable to prepare annotation data for export. Please try again.",
        originalError: error.message
      });
    }

    // Step 8: Prepare private tags
    const groupHex = privateGroup.toString(16).padStart(4, "0");
    const creatorElemHex = (0x0010).toString(16).padStart(4, "0"); // private creator element
    const privateElemHex = (0x1001).toString(16).padStart(4, "0"); // chosen private element
    const creatorKey = `x${groupHex}${creatorElemHex}`;
    const privateKey = `x${groupHex}${privateElemHex}`;

    console.log("Will insert keys:", creatorKey, privateKey);

    // Check for existing private tags that might conflict
    if (dict[creatorKey] || dict[privateKey]) {
      console.warn("Private tag conflict detected, using alternative group");
      // Could implement fallback to different group here if needed
    }

    // Step 9: Insert private creator and annotation data
    try {
      dict[creatorKey] = { vr: "LO", Value: [privateCreator] };
      dict[privateKey] = { vr: "LT", Value: [annotationsJson] };
      
      console.log("Successfully inserted private tags:");
      console.log(`${creatorKey}:`, dict[creatorKey]);
      console.log(`${privateKey}:`, dict[privateKey]);
      console.log("Dict keys after insertion:", Object.keys(dict).filter(k => k.includes('0029')));
    } catch (error) {
      throw new DicomProcessingError("Failed to insert annotation data into DICOM", {
        userMessage: "Unable to embed annotations into the DICOM file. The file structure may be incompatible.",
        originalError: error.message
      });
    }

    // Step 10: Write final DICOM file
    console.log("About to write DICOM file. Final dict keys containing 0029:", Object.keys(dict).filter(k => k.includes('0029')));
    console.log("Final dict private tags:", {
      [creatorKey]: dict[creatorKey],
      [privateKey]: dict[privateKey]
    });
    
    let part10;
    try {
      // Create a new DicomMessage instance and write it
      // This approach is more compatible with different dcmjs versions
      const outputMessage = Object.assign(Object.create(Object.getPrototypeOf(dicomMessage)), dicomMessage);
      outputMessage.dict = dict;
      outputMessage.meta = meta;
      
      part10 = outputMessage.write();
    } catch (error) {
      console.warn("Primary write method failed, trying alternative approach:", error.message);
      
      try {
        // Alternative: Use the original static method but ensure proper structure
        part10 = dcmjs.data.DicomMessage.write(dict, meta);
      } catch (fallbackError) {
        console.warn("Static write method also failed, trying manual construction:", fallbackError.message);
        
        try {
          // Last resort: Manual construction with minimal meta header
          const minimalMeta = {
            '00020001': { vr: 'OB', Value: [new Uint8Array([0, 1])] },
            '00020002': { vr: 'UI', Value: [dict['x00080016']?.Value?.[0] || '1.2.840.10008.5.1.4.1.1.2'] },
            '00020003': { vr: 'UI', Value: [dict['x00080018']?.Value?.[0] || '1.2.3.4.5.6.7.8.9'] },
            '00020010': { vr: 'UI', Value: ['1.2.840.10008.1.2.1'] },
            '00020012': { vr: 'UI', Value: ['1.2.840.10008.1.2.1'] },
            '00020013': { vr: 'SH', Value: ['DCMJS'] }
          };
          
          // Merge with existing meta if available
          const finalMeta = { ...minimalMeta, ...meta };
          
          // Create new message with clean structure
          const newMessage = new dcmjs.data.DicomMessage(finalMeta, dict);
          part10 = newMessage.write();
          
        } catch (lastError) {
          throw new DicomProcessingError("Failed to generate final DICOM file", {
            userMessage: "Unable to create the final DICOM file. The DICOM library encountered compatibility issues. Please try with a different DICOM file or contact support.",
            originalError: `All methods failed. Last error: ${lastError.message}`
          });
        }
      }
    }

    // Validate the output size
    if (!part10 || part10.byteLength === 0) {
      throw new DicomProcessingError("Generated DICOM file is empty", {
        userMessage: "The export process completed but generated an empty file. Please try again."
      });
    }

    // For testing, allow skipping the download
    if (options.skipDownload) {
      return { 
        success: true, 
        data: part10, 
        annotations: built,
        stats: {
          totalAnnotations,
          invalidAnnotations,
          correctedAnnotations: correctedAnnotations || 0,
          validationWarnings: validationWarnings || 0,
          validationErrors: validationErrors || 0,
          framesWithAnnotations: Object.keys(built).length,
          outputSize: part10.byteLength
        }
      };
    }

    // Step 11: Trigger download with error handling
    try {
      const blob = new Blob([part10], { type: "application/dicom" });
      const url = URL.createObjectURL(blob);
      const filename = (file.name ? file.name.replace(/\.dcm$/i, "") : "annotated") + "_annotated.dcm";
      
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      console.log(`Successfully exported DICOM file: ${filename} (${part10.byteLength} bytes)`);
    } catch (error) {
      throw new DicomProcessingError("Failed to download exported file", {
        userMessage: "The DICOM file was created successfully but could not be downloaded. Please check your browser's download settings.",
        originalError: error.message
      });
    }

    return { 
      success: true, 
      annotations: built,
      stats: {
        totalAnnotations,
        invalidAnnotations,
        correctedAnnotations: correctedAnnotations || 0,
        validationWarnings: validationWarnings || 0,
        validationErrors: validationErrors || 0,
        framesWithAnnotations: Object.keys(built).length,
        outputSize: part10.byteLength
      }
    };

  } catch (error) {
    // Enhanced error logging
    console.error("exportAnnotatedDicom error:", {
      name: error.name,
      message: error.message,
      code: error.code,
      details: error.details,
      stack: error.stack
    });

    // Re-throw with enhanced context if it's not already our custom error
    if (!(error instanceof DicomExportError)) {
      throw new DicomProcessingError("Unexpected error during DICOM export", {
        userMessage: "An unexpected error occurred during export. Please try again or contact support if the problem persists.",
        originalError: error.message
      });
    }
    
    throw error;
  }
}
